import React, { useEffect, useState } from 'react'
import { CheckCircle2, Link, MessageSquare, ShieldCheck, X } from 'lucide-react'
import type {
  SpreadsheetValidationErrorStyle,
  SpreadsheetValidationRule,
  SpreadsheetValidationRuleKind,
  SpreadsheetValidationRuleOperator,
} from '../../protocol/spreadsheetEngineProtocol'
import {
  CELL_METADATA_DIALOG_COPY,
  buildCellMetadataDialogApplyPlan,
  buildCellMetadataDialogClearPlan,
  buildCellMetadataDialogInitialState,
  describeHyperlinkPreview,
  type CellMetadataDialogApplyPlan,
  type CellMetadataDialogClearPlan,
} from '../../spreadsheet/cellMetadataPresentation'
import {
  buildValidationRuleAuthoringApplyPlan,
  buildValidationRuleAuthoringDefaults,
  buildValidationRuleAuthoringModel,
  type ExcelValidationRuleDraft,
  type ValidationListSourceMode,
  type ValidationRuleAuthoringState,
} from '../../spreadsheet/validationRuleAuthoring'

export type ExcelCellMetadataMode = 'comment' | 'hyperlink' | 'protection' | 'validation'
export type { ExcelValidationRuleDraft } from '../../spreadsheet/validationRuleAuthoring'

type ExcelCellMetadataDialogProps = {
  mode: ExcelCellMetadataMode
  selectionLabel: string
  currentComment?: string | null
  currentHyperlink?: string | null
  currentProtection?: Record<string, unknown> | null
  currentValidationId?: string | null
  currentValidationRule?: SpreadsheetValidationRule | null
  onClose: () => void
  onApplyComment: (comment: string | null) => void
  onApplyHyperlink: (hyperlink: string | null) => void
  onApplyProtection: (protection: Record<string, unknown> | null) => void
  onApplyValidation: (validationId: string | null) => void
  onApplyValidationRule: (rule: ExcelValidationRuleDraft) => void
}

function MetadataIcon({ mode }: { mode: ExcelCellMetadataMode }) {
  if (mode === 'hyperlink') return <Link size={18} />
  if (mode === 'protection') return <ShieldCheck size={18} />
  if (mode === 'validation') return <CheckCircle2 size={18} />
  return <MessageSquare size={18} />
}

export function ExcelCellMetadataDialog({
  mode,
  selectionLabel,
  currentComment,
  currentHyperlink,
  currentProtection,
  currentValidationId,
  currentValidationRule,
  onClose,
  onApplyComment,
  onApplyHyperlink,
  onApplyProtection,
  onApplyValidation,
  onApplyValidationRule,
}: ExcelCellMetadataDialogProps) {
  const [value, setValue] = useState('')
  const [locked, setLocked] = useState(true)
  const [validationKind, setValidationKind] = useState<SpreadsheetValidationRuleKind>('list')
  const [validationOperator, setValidationOperator] = useState<SpreadsheetValidationRuleOperator>('between')
  const [validationFormula1, setValidationFormula1] = useState('0')
  const [validationFormula2, setValidationFormula2] = useState('100')
  const [validationValues, setValidationValues] = useState('Yes, No')
  const [validationListSourceMode, setValidationListSourceMode] = useState<ValidationListSourceMode>('values')
  const [validationAllowBlank, setValidationAllowBlank] = useState(true)
  const [validationInputTitle, setValidationInputTitle] = useState('')
  const [validationInputMessage, setValidationInputMessage] = useState('')
  const [validationErrorStyle, setValidationErrorStyle] = useState<SpreadsheetValidationErrorStyle>('stop')
  const [validationErrorTitle, setValidationErrorTitle] = useState('')
  const [validationErrorMessage, setValidationErrorMessage] = useState('')
  const copy = CELL_METADATA_DIALOG_COPY[mode]
  const hyperlinkPreview = mode === 'hyperlink' ? describeHyperlinkPreview(value) : null
  const validationRuleState: ValidationRuleAuthoringState = {
    id: value,
    kind: validationKind,
    operator: validationOperator,
    formula1: validationFormula1,
    formula2: validationFormula2,
    listValuesText: validationValues,
    listSourceMode: validationListSourceMode,
    allowBlank: validationAllowBlank,
    inputTitle: validationInputTitle,
    inputMessage: validationInputMessage,
    errorStyle: validationErrorStyle,
    errorTitle: validationErrorTitle,
    errorMessage: validationErrorMessage,
  }
  const validationRuleModel = mode === 'validation'
    ? buildValidationRuleAuthoringModel({
      currentRuleId: currentValidationRule?.id ?? null,
      state: validationRuleState,
      applyLabel: copy.applyLabel,
    })
    : null
  const validationRulePresentation = validationRuleModel?.presentation ?? null
  const validationRuleIssues = validationRuleModel?.issues ?? []
  const validationRulePreview = validationRuleModel?.preview ?? null
  const validationRuleActionState = validationRuleModel?.actionState ?? null

  useEffect(() => {
    if (mode === 'validation') {
      const defaults = buildValidationRuleAuthoringDefaults({
        currentValidationId,
        currentValidationRule,
      })
      setValue(defaults.id)
      setValidationKind(defaults.kind)
      setValidationOperator(defaults.operator)
      setValidationFormula1(defaults.formula1)
      setValidationFormula2(defaults.formula2)
      setValidationListSourceMode(defaults.listSourceMode)
      setValidationValues(defaults.listValuesText)
      setValidationAllowBlank(defaults.allowBlank)
      setValidationInputTitle(defaults.inputTitle)
      setValidationInputMessage(defaults.inputMessage)
      setValidationErrorStyle(defaults.errorStyle)
      setValidationErrorTitle(defaults.errorTitle)
      setValidationErrorMessage(defaults.errorMessage)
    } else {
      const state = buildCellMetadataDialogInitialState({
        mode,
        currentComment,
        currentHyperlink,
        currentProtection,
      })
      setValue(state.value)
      setLocked(state.locked)
    }
  }, [currentComment, currentHyperlink, currentProtection, currentValidationId, currentValidationRule, mode])

  const executeApplyPlan = (plan: CellMetadataDialogApplyPlan): boolean => {
    switch (plan.action) {
      case 'apply-comment':
        onApplyComment(plan.comment)
        return true
      case 'apply-hyperlink':
        onApplyHyperlink(plan.hyperlink)
        return true
      case 'apply-protection':
        onApplyProtection(plan.protection)
        return true
      case 'apply-validation-rule':
        onApplyValidationRule(plan.rule)
        return true
      case 'blocked':
        return false
    }
  }

  const executeClearPlan = (plan: CellMetadataDialogClearPlan) => {
    switch (plan.action) {
      case 'apply-comment':
        onApplyComment(plan.comment)
        break
      case 'apply-hyperlink':
        onApplyHyperlink(plan.hyperlink)
        break
      case 'apply-protection':
        onApplyProtection(plan.protection)
        break
      case 'apply-validation':
        onApplyValidation(plan.validationId)
        break
    }
  }

  const apply = () => {
    const validationApplyPlan = validationRuleModel
      ? buildValidationRuleAuthoringApplyPlan(validationRuleModel.draftInput)
      : null
    const applied = executeApplyPlan(buildCellMetadataDialogApplyPlan({
      mode,
      value,
      locked,
      validationApplyPlan,
    }))
    if (!applied) return
    onClose()
  }

  const clear = () => {
    executeClearPlan(buildCellMetadataDialogClearPlan(mode))
    onClose()
  }

  return (
    <div className="fg-excel-format-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="fg-excel-cell-metadata-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={copy.title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="fg-excel-format-dialog-header">
          <span className="fg-excel-cell-metadata-title">
            <MetadataIcon mode={mode} />
            <strong>{copy.title}</strong>
          </span>
          <button type="button" className="fg-excel-icon-button" aria-label={`Close ${copy.title}`} title={`Close ${copy.title}`} onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <div className="fg-excel-cell-metadata-range" title={`Selection ${selectionLabel}`}>
          {selectionLabel}
        </div>

        {mode === 'protection' ? (
          <label className="fg-excel-cell-metadata-check">
            <input
              type="checkbox"
              checked={locked}
              title={copy.fieldLabel}
              onChange={(event) => setLocked(event.target.checked)}
            />
            <span>{copy.fieldLabel}</span>
          </label>
        ) : mode === 'comment' ? (
          <label className="fg-excel-cell-metadata-field">
            <span>{copy.fieldLabel}</span>
            <textarea
              value={value}
              onChange={(event) => setValue(event.target.value)}
              autoFocus
              rows={5}
              title={copy.fieldLabel}
            />
          </label>
        ) : mode === 'hyperlink' ? (
          <>
            <label className="fg-excel-cell-metadata-field">
              <span>{copy.fieldLabel}</span>
              <input
                value={value}
                onChange={(event) => setValue(event.target.value)}
                autoFocus
                inputMode="url"
                placeholder="https://"
                title={copy.fieldLabel}
              />
            </label>
            {hyperlinkPreview ? (
              <div
                className={`fg-excel-hyperlink-preview${hyperlinkPreview.warning ? ' is-warning' : ''}`}
                data-testid="fg-excel-hyperlink-preview"
                title={hyperlinkPreview.detail}
              >
                <span>{hyperlinkPreview.warning ?? 'Destination'}</span>
                <strong>{hyperlinkPreview.label}</strong>
                <small>{hyperlinkPreview.detail}</small>
              </div>
            ) : null}
          </>
        ) : (
          <div className="fg-excel-cell-metadata-field">
            {validationRulePresentation?.currentRuleLabel ? (
              <div className="fg-excel-cell-metadata-range" title={`Current rule ${value}`}>
                {validationRulePresentation.currentRuleLabel}
              </div>
            ) : null}
            <label className="fg-excel-cell-metadata-field">
              <span>{validationRulePresentation?.kindField.label}</span>
              <select
                value={validationKind}
                onChange={(event) => setValidationKind(event.target.value as SpreadsheetValidationRuleKind)}
                autoFocus
                title={validationRulePresentation?.kindField.title}
              >
                {validationRulePresentation?.kindField.options.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            {validationRulePresentation?.showsListSource ? (
              <>
                <fieldset className="fg-excel-cell-metadata-source-mode">
                  <legend title={validationRulePresentation.listSourceModeField.title}>
                    {validationRulePresentation.listSourceModeField.label}
                  </legend>
                  {validationRulePresentation.listSourceModeField.options.map((option) => (
                    <label key={option.value} title={option.title}>
                      <input
                        type="radio"
                        name="fg-excel-validation-list-source"
                        value={option.value}
                        checked={validationListSourceMode === option.value}
                        title={option.title}
                        onChange={() => setValidationListSourceMode(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </fieldset>
                <label className="fg-excel-cell-metadata-field">
                  <span>{validationRulePresentation.sourceField.label}</span>
                  <input
                    value={validationValues}
                    onChange={(event) => setValidationValues(event.target.value)}
                    placeholder={validationRulePresentation.sourceField.placeholder}
                    title={validationRulePresentation.sourceField.title}
                  />
                </label>
              </>
            ) : validationRulePresentation?.showsCustomFormula ? (
              <label className="fg-excel-cell-metadata-field">
                <span>{validationRulePresentation.firstFormulaField.label}</span>
                <input
                  value={validationFormula1}
                  onChange={(event) => setValidationFormula1(event.target.value)}
                  placeholder={validationRulePresentation.firstFormulaField.placeholder}
                  inputMode={validationRulePresentation.firstFormulaField.inputMode}
                  title={validationRulePresentation.firstFormulaField.title}
                />
              </label>
            ) : validationRulePresentation?.showsComparison ? (
              <>
                <label className="fg-excel-cell-metadata-field">
                  <span>{validationRulePresentation.operatorField.label}</span>
                  <select
                    value={validationOperator}
                    onChange={(event) => setValidationOperator(event.target.value as SpreadsheetValidationRuleOperator)}
                    title={validationRulePresentation.operatorField.title}
                  >
                    {validationRulePresentation.operatorField.options.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="fg-excel-cell-metadata-field">
                  <span>{validationRulePresentation.firstFormulaField.label}</span>
                  <input
                    value={validationFormula1}
                    onChange={(event) => setValidationFormula1(event.target.value)}
                    inputMode={validationRulePresentation.firstFormulaField.inputMode}
                    title={validationRulePresentation.firstFormulaField.title}
                  />
                </label>
                {validationRulePresentation.showsSecondFormula ? (
                  <label className="fg-excel-cell-metadata-field">
                    <span>{validationRulePresentation.secondFormulaField.label}</span>
                    <input
                      value={validationFormula2}
                      onChange={(event) => setValidationFormula2(event.target.value)}
                      inputMode={validationRulePresentation.secondFormulaField.inputMode}
                      title={validationRulePresentation.secondFormulaField.title}
                    />
                  </label>
                ) : null}
              </>
            ) : null}
            <label className="fg-excel-cell-metadata-check">
              <input
                type="checkbox"
                checked={validationAllowBlank}
                title={validationRulePresentation?.allowBlankField.title}
                onChange={(event) => setValidationAllowBlank(event.target.checked)}
              />
              <span>{validationRulePresentation?.allowBlankField.label}</span>
            </label>
            <label className="fg-excel-cell-metadata-field">
              <span>{validationRulePresentation?.inputTitleField.label}</span>
              <input
                value={validationInputTitle}
                onChange={(event) => setValidationInputTitle(event.target.value)}
                placeholder={validationRulePresentation?.inputTitleField.placeholder}
                title={validationRulePresentation?.inputTitleField.title}
              />
            </label>
            <label className="fg-excel-cell-metadata-field">
              <span>{validationRulePresentation?.inputMessageField.label}</span>
              <input
                value={validationInputMessage}
                onChange={(event) => setValidationInputMessage(event.target.value)}
                placeholder={validationRulePresentation?.inputMessageField.placeholder}
                title={validationRulePresentation?.inputMessageField.title}
              />
            </label>
            <label className="fg-excel-cell-metadata-field">
              <span>{validationRulePresentation?.errorStyleField.label}</span>
              <select
                value={validationErrorStyle}
                onChange={(event) => setValidationErrorStyle(event.target.value as SpreadsheetValidationErrorStyle)}
                title={validationRulePresentation?.errorStyleField.title}
              >
                {validationRulePresentation?.errorStyleField.options.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="fg-excel-cell-metadata-field">
              <span>{validationRulePresentation?.errorTitleField.label}</span>
              <input
                value={validationErrorTitle}
                onChange={(event) => setValidationErrorTitle(event.target.value)}
                placeholder={validationRulePresentation?.errorTitleField.placeholder}
                title={validationRulePresentation?.errorTitleField.title}
              />
            </label>
            <label className="fg-excel-cell-metadata-field">
              <span>{validationRulePresentation?.errorMessageField.label}</span>
              <input
                value={validationErrorMessage}
                onChange={(event) => setValidationErrorMessage(event.target.value)}
                placeholder={validationRulePresentation?.errorMessageField.placeholder}
                title={validationRulePresentation?.errorMessageField.title}
              />
            </label>
            {validationRuleIssues.length > 0 ? (
              <div
                className="fg-excel-cell-metadata-validation-error"
                data-testid="fg-excel-validation-rule-error"
                role="alert"
                title={validationRuleIssues.map((issue) => issue.message).join(' ')}
              >
                {validationRuleIssues[0]?.message}
              </div>
            ) : null}
            {validationRulePreview ? (
              <div
                className="fg-excel-cell-metadata-validation-preview"
                data-testid="fg-excel-validation-rule-preview"
                title={validationRulePreview.lines.join(' ')}
              >
                <strong>{validationRulePreview.title}</strong>
                <ul>
                  {validationRulePreview.lines.map((line, index) => (
                    <li key={`${index}:${line}`}>{line}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}

        <footer className="fg-excel-cell-metadata-footer">
          <button type="button" className="fg-excel-cell-metadata-button" title={copy.clearLabel} onClick={clear}>
            {copy.clearLabel}
          </button>
          <button
            type="button"
            className="fg-excel-cell-metadata-button fg-excel-cell-metadata-button-primary"
            title={validationRuleActionState?.applyTitle ?? copy.applyLabel}
            disabled={validationRuleActionState?.canApply === false}
            onClick={apply}
          >
            {copy.applyLabel}
          </button>
        </footer>
      </section>
    </div>
  )
}
